// Live Updates (Capgo, 셀프호스팅 수동 모드).
// 네이티브 앱이 켜질 때 hyuh.app/updates/latest.json을 확인 → 새 JS/CSS 번들이면
// 백그라운드로 받아두고, 앱이 백그라운드로 갈 때 적용(다음 실행부터 새 버전).
// 스토어 재심사 없이 JS/CSS 수정 배포. 네이티브 변경(플러그인·권한·SDK)은 여전히 스토어 재빌드.
//
// 안전장치: 새 번들이 켜진 뒤 notifyAppReady()를 호출 못 하면(크래시 등)
// appReadyTimeout(10초) 후 네이티브가 직전 동작 번들로 자동 롤백 → 절대 안 브릭됨.

import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { CapacitorUpdater } from "@capgo/capacitor-updater";

// 이 네이티브 빌드에 '이미 번들된' OTA 버전.
// 스토어 릴리스를 새로 낼 때마다, 그 빌드가 포함한 최신 OTA 번호로 올린다
// (그래야 막 설치한 사용자가 이미 들어있는 번들을 다시 안 받음).
const OTA_BASELINE = 1;

const MANIFEST_URL = "https://hyuh.app/updates/latest.json";

let pendingBundleId: string | null = null;
let listenerAttached = false;

/**
 * 새 번들이 정상 로드됐음을 네이티브에 알림. 앱 시작 시 가능한 한 빨리 호출.
 * 호출 안 되면 네이티브가 직전 번들로 롤백하므로, 깨진 OTA가 사용자를 가두지 못함.
 */
export async function notifyAppReady(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await CapacitorUpdater.notifyAppReady();
  } catch {
    /* 무시 — 실패해도 앱 동작엔 영향 없음 */
  }
}

/**
 * 매니페스트 확인 → 새 버전이면 백그라운드 다운로드. 적용은 앱이 백그라운드 갈 때(부드러운 전환).
 * 네이티브에서만 동작. 어떤 실패도 조용히 삼킨다(업데이트는 best-effort, 앱은 절대 안 깨짐).
 */
export async function initLiveUpdates(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  if (!listenerAttached) {
    listenerAttached = true;
    // 앱이 백그라운드로 가는 순간, 받아둔 번들을 적용 → 다음에 열 때 새 버전(세션 중 끊김 없음).
    void App.addListener("appStateChange", ({ isActive }) => {
      if (!isActive && pendingBundleId) {
        const id = pendingBundleId;
        pendingBundleId = null;
        void CapacitorUpdater.set({ id }).catch(() => {});
      }
    });
  }

  await checkForUpdate();
}

async function checkForUpdate(): Promise<void> {
  try {
    const res = await fetch(MANIFEST_URL, { cache: "no-store" });
    if (!res.ok) return;
    const m = (await res.json()) as { version?: string | number; url?: string };
    const remote = Number(m.version);
    if (!m.url || !Number.isFinite(remote)) return;

    // 네이티브에 이미 포함된(또는 더 옛) 버전이면 받지 않음.
    if (remote <= OTA_BASELINE) return;

    // 이미 같은 OTA 번들을 적용 중이면 스킵.
    const cur = await CapacitorUpdater.current();
    if (cur.bundle.version === String(remote)) return;

    const bundle = await CapacitorUpdater.download({ url: m.url, version: String(remote) });
    pendingBundleId = bundle.id; // 백그라운드 전환 때 set()으로 적용
  } catch {
    /* 네트워크/파싱/다운로드 실패 → 조용히 무시. 다음 실행 때 다시 시도. */
  }
}
